import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, ImageList, ImageListItem,
  IconButton, Typography, Dialog, DialogContent,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { getUploadUrl, uploadToBlob } from '../services/api';

export default function PhotoUpload({ tripId, activityId, photoUrls = [], onPhotosChange, canDelete = false }) {
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const { data } = await getUploadUrl(tripId, activityId, file.name, file.type);
        await uploadToBlob(data.uploadUrl, file);
        newUrls.push(data.blobUrl);
      }
      onPhotosChange([...photoUrls, ...newUrls]);
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (url) => {
    onPhotosChange(photoUrls.filter((u) => u !== url));
  };

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevPhoto = () => setLightboxIndex((i) => (i - 1 + photoUrls.length) % photoUrls.length);
  const nextPhoto = () => setLightboxIndex((i) => (i + 1) % photoUrls.length);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') prevPhoto();
    if (e.key === 'ArrowRight') nextPhoto();
    if (e.key === 'Escape') closeLightbox();
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Button
          variant="outlined"
          component="label"
          startIcon={uploading ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Add Photos'}
          <input type="file" accept="image/*" multiple hidden onChange={handleFileChange} />
        </Button>
        <Typography variant="caption" color="text.secondary">
          {photoUrls.length} photo{photoUrls.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {photoUrls.length > 0 && (
        <ImageList cols={3} rowHeight={120} gap={8}>
          {photoUrls.map((url, index) => (
            <ImageListItem key={url} sx={{ position: 'relative', cursor: 'pointer' }}>
              <img
                src={url} alt=""
                style={{ objectFit: 'cover', height: 120, borderRadius: 4 }}
                onClick={() => openLightbox(index)}
              />
              {canDelete && (
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.5)', color: 'white' }}
                  onClick={(e) => { e.stopPropagation(); handleDelete(url); }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Lightbox */}
      <Dialog
        open={lightboxIndex !== null}
        onClose={closeLightbox}
        maxWidth={false}
        onKeyDown={handleKeyDown}
        PaperProps={{
          sx: { bgcolor: 'rgba(0,0,0,0.95)', boxShadow: 'none', m: 0, maxWidth: '100vw', maxHeight: '100vh' },
        }}
        sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(0,0,0,0.95)' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '60vw', minHeight: '60vh' }}>
          {lightboxIndex !== null && (
            <img
              src={photoUrls[lightboxIndex]}
              alt=""
              style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 4 }}
            />
          )}

          {/* Close */}
          <IconButton
            onClick={closeLightbox}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
          >
            <CloseIcon />
          </IconButton>

          {/* Prev */}
          {photoUrls.length > 1 && (
            <IconButton
              onClick={prevPhoto}
              sx={{ position: 'absolute', left: 8, color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}

          {/* Next */}
          {photoUrls.length > 1 && (
            <IconButton
              onClick={nextPhoto}
              sx={{ position: 'absolute', right: 8, color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}

          {/* Counter */}
          {photoUrls.length > 1 && (
            <Typography
              sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}
            >
              {lightboxIndex + 1} / {photoUrls.length}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
